import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Component } from "react";
import { LanguageProvider } from "./context/LanguageContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsAppFloat from "./components/WhatsAppFloat";
import Home from "./pages/Home";
import Dealers from "./pages/Dealers";
import PrivateClients from "./pages/PrivateClients";
import SellYourCar from "./pages/SellYourCar";
import Cars from "./pages/Cars";
import CarDetail from "./pages/CarDetail";
import CookieBanner from "./components/CookieBanner";
import Process from "./pages/Process";
import Network from "./pages/Network";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Legal from "./pages/Legal";
import Admin from "./pages/Admin";
import Seo from "./components/Seo";
import { SEO_BY_PATH } from "./seoConfig";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("Boundary:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#050607] flex items-center justify-center px-6">
          <div className="text-center">
            <div className="wordmark text-2xl text-[#F3F3F0] mb-4">VEYRONE</div>
            <p className="mono text-[12px] text-[#999995]">Something went wrong. Please reload the page.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LenisSetup() {
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;
    let lenis;
    let rafId;
    import("lenis").then(({ default: Lenis }) => {
      lenis = new Lenis({ duration: 1.15, smoothWheel: true });
      window.__lenis = lenis;
      const raf = (time) => {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (lenis) lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function PageSeo() {
  const { pathname } = useLocation();
  const clean = pathname.replace(/\/+$/, "") || "/";
  const data = SEO_BY_PATH[clean];
  if (!data) return null;
  return <Seo title={data.title} description={data.description} path={clean} noindex={data.noindex} />;
}

function SiteChrome() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      <PageSeo />
      <ScrollToTop />
      {!isAdmin && <CookieBanner />}
      {!isAdmin && <Header />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/dealers" element={<Dealers />} />
          <Route path="/private-clients" element={<PrivateClients />} />
          <Route path="/sell-your-car" element={<SellYourCar />} />
          <Route path="/cars" element={<Cars />} />
          <Route path="/cars/:id" element={<CarDetail />} />
          <Route path="/our-process" element={<Process />} />
          <Route path="/global-network" element={<Network />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<Legal doc="privacy" />} />
          <Route path="/terms" element={<Legal doc="terms" />} />
          <Route path="/cookie-policy" element={<Legal doc="cookies" />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <WhatsAppFloat />}
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <BrowserRouter>
          <LenisSetup />
          <SiteChrome />
        </BrowserRouter>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
